/**
 * <TutorStudentHeader> — l'en-tête de la fiche d'un élève (organism).
 *
 * Ce qui reste sous les yeux d'un onglet à l'autre : son visage, son nom, la ligne grise
 * (« 4ème · 18 séances · Membre depuis sept. 2025 »), les matières suivies avec ce masteur, une ligne
 * par matière avec sa séance et le type de cours (`<SubjectBadge>`), puis « Nouvel élève » et ses
 * besoins particuliers durables, en chips. La matière se lit par élève : un cours partagé ne montre
 * que la sienne (Wajih, 29/10). Sans cours, ses matières prioritaires, comme l'ancienne fiche.
 *
 * `dense` : à l'étroit, la matière s'abrège pour tenir sur la ligne de sa séance. `compact` : sur un
 * onglet qui a besoin de la hauteur, à l'étroit, l'en-tête garde qui il est et ses besoins, et rend ses
 * cours à l'Aperçu. Pas une carte : la fiche pose ses onglets juste dessous.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorStudentCourse, type UITutorStudentProfile } from "../../../ports/primary/ui/types/tutorEleves"

/** L'avatar de la fiche, puis celui de l'en-tête resserré. */
export const TUTOR_PROFILE_AVATAR_SIZE = 64
const TUTOR_PROFILE_AVATAR_COMPACT_SIZE = 44

const COURSE_KIND: Record<UITutorStudentCourse["kind"], string> = {
    shared: "cours partagé",
    individual: "individuel",
}

export type TutorStudentHeaderProps = {
    profile: UITutorStudentProfile
    compact?: boolean
    dense?: boolean
    /** Au bout de l'en-tête : « Activité ». Absent : rien. */
    trailing?: React.ReactNode
    testID?: string
}

function metaLine(profile: UITutorStudentProfile): string {
    const parts: string[] = []
    if (profile.student.classLabel) parts.push(profile.student.classLabel)
    if (profile.sessionCount != null) parts.push(countLabel(profile.sessionCount, "séance"))
    if (profile.memberSinceLabel) parts.push(`Membre depuis ${profile.memberSinceLabel}`)
    return parts.join(" · ")
}

function CourseLine(props: { course: UITutorStudentCourse; dense: boolean }) {
    const { course, dense } = props
    return (
        <Row gap="2" align="center" style={styles.wrap}>
            <SubjectBadge subject={course.subject} showLabel fullLabel={!dense} />
            <Text size="text-sm">{`${course.scheduleLabel} · ${COURSE_KIND[course.kind]}`}</Text>
            {course.live === true && <Badge text="En cours" variant="success" tinted />}
            {course.status === "requested" && <Badge text="Demandé" variant="warning" tinted />}
        </Row>
    )
}

export function TutorStudentHeader(props: TutorStudentHeaderProps) {
    const { profile, compact = false, dense = false, trailing, testID } = props
    const { student, specialNeeds, isNew } = profile
    const courses = student.courses ?? []
    const meta = metaLine(profile)
    const hasChips = isNew === true || specialNeeds.length > 0

    return (
        <Row gap={compact ? "3" : "4"} align="start" testID={testID}>
            <Avatar
                uri={student.avatarUrl}
                name={student.name}
                size={compact ? TUTOR_PROFILE_AVATAR_COMPACT_SIZE : TUTOR_PROFILE_AVATAR_SIZE}
            />
            <Stack gap="2" style={styles.body}>
                <Stack gap="0">
                    <Title size={compact ? "text-lg" : "text-2xl"}>{student.name}</Title>
                    {meta !== "" && (
                        <Text size="text-sm" color="text-muted">
                            {meta}
                        </Text>
                    )}
                </Stack>
                {!compact &&
                    (courses.length > 0 ? (
                        <Stack gap="1">
                            {courses.map((course) => (
                                <CourseLine key={course.id} course={course} dense={dense} />
                            ))}
                        </Stack>
                    ) : (
                        student.subjects.length > 0 && <SubjectBadges subjects={student.subjects} />
                    ))}
                {hasChips && (
                    <Row gap="2" style={styles.wrap}>
                        {isNew === true && <Badge text="Nouvel élève" variant="info" />}
                        {specialNeeds.map((need) => (
                            <Badge key={need} text={need} variant="warning" />
                        ))}
                    </Row>
                )}
            </Stack>
            {trailing}
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" },
}))
